import { graphql, useStaticQuery, withPrefix } from 'gatsby'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { StoreDispatch, StoreState } from '../../Store'

// Components
import Button from '../Button'
import MenuToggle from '../MenuToggle'
import PageTransitionLink from '../PageTransitionLink'

// Vars
import { colors } from '../../styles/vars/colors.style'
import { Heading2 } from '../../styles/vars/textStyles.style'
import { Cta } from '../TextStyles'

// SVGs
import Logo from '../svgs/Logo'
import UwcLogo from '../svgs/UwcLogo'

// Styles
import {
  HeaderContainer,
  HeaderInner,
  HeaderInnerLine,
  HeaderLogo,
  HeaderMain,
  HeaderMenu,
  HeaderMenuItem,
  HeaderNav,
  HeaderUwcLogo,
  HeaderUwcLogoMobile,
  HeaderVerticalLine,
  MobileMenu,
  MobileMenuItem,
  MobileNav,
  MobileNavMain,
} from './index.style'

// Functions
import { countryCookieCheck, scrollToId } from '../../utils/utils'

const Header = ({ pathname }) => {
  const [pageHasScrolled, setPageHasScrolled] = useState(false)
  const [pageIsScrolled, setPageIsScrolled] = useState(false)
  const pageIsScrolledRef = useRef(false)
  const [linkSuffix, setLinkSuffix] = useState('')

  const store = useContext(StoreState)
  const dispatch = useContext(StoreDispatch)
  const { mobileNavIsOpen, waiting, scroll } = store

  useEffect(() => {
    if (store.countryCookie !== null) {
      const suffix = countryCookieCheck(store.countryCookie)
      setLinkSuffix(suffix)
    }
  }, [store.countryCookie])

  const {
    site: {
      siteMetadata: { routes },
    },
    allContentfulGlobalSettings,
  } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          routes {
            home
            expeditions
            itinerary
          }
        }
      }
      allContentfulGlobalSettings(limit: 1) {
        nodes {
          reservationContactNumber
          callToReserve
          overview
          expeditions
          itinerary
          faq
        }
      }
    }
  `)

  const {
    callToReserve,
    overview,
    expeditions,
    itinerary,
    faq,
  } = allContentfulGlobalSettings.nodes[0]

  const scrollToFaq = e => {
    e.preventDefault()
    !isMobile && setPageHasScrolled(true)
    !isMobile && setPageIsScrolled(true)
    pageIsScrolledRef.current = true
    scrollToId('faq', dispatch)
  }

  const scrollToWtb = e => {
    e.preventDefault()
    !isMobile && setPageHasScrolled(true)
    !isMobile && setPageIsScrolled(true)
    pageIsScrolledRef.current = true
    scrollToId('ways-to-book', dispatch)
  }

  /*
   ** On scroll, check if page has scrolled more than scrollAmount
   ** Update pageIsScrolled state
   */

  useEffect(() => {
    if (!scroll) return

    const scrollThreshold = 80

    const scrollFunction = () => {
      if (
        scroll.scroll.instance.scroll.y > scrollThreshold &&
        !pageIsScrolledRef.current
      ) {
        setPageHasScrolled(true)
        setPageIsScrolled(true)
        pageIsScrolledRef.current = true
      } else if (
        scroll.scroll.instance.scroll.y <= scrollThreshold &&
        pageIsScrolledRef.current
      ) {
        setPageIsScrolled(false)
        pageIsScrolledRef.current = false
      }
    }

    !isMobile && scroll.on('scroll', scrollFunction)

    return () => {
      !isMobile && scroll.off('scroll', scrollFunction)
    }
  }, [scroll])

  /*
   ** Reset pageIsScrolled state when pathname is updated just in case it doesn't reset on route change
   */
  useEffect(() => {
    setPageIsScrolled(false)
    pageIsScrolledRef.current = false

    const expeditionsPath = '/expeditions/'

    if (
      pathname.indexOf(expeditionsPath) > -1 &&
      pathname.length > withPrefix(expeditionsPath).length
    ) {
      dispatch({ type: 'SET_HEADER_DARK' })
    } else {
      dispatch({ type: 'SET_HEADER_LIGHT' })
    }
  }, [pathname, dispatch])

  return (
    <HeaderContainer
      scrolled={pageIsScrolled}
      dark={mobileNavIsOpen ? false : store.headerDark}
    >
      <HeaderInner>
        <HeaderInnerLine
          ready={!waiting}
          scrolled={pageIsScrolled}
          extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
        />
        <HeaderMain>
          <HeaderLogo
            ready={!waiting}
            extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
            scrolled={pageIsScrolled}
          >
            <a href={`https://www.royalcaribbean.com/${linkSuffix}`}>
              <Logo fill="currentColor" responsive />
            </a>

            <MenuToggle
              ready={!waiting}
              extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
              scrolled={pageIsScrolled}
            />

            <HeaderVerticalLine
              delay={0.4}
              right={true}
              longDelay={3}
              mobile={true}
              extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
              scrolled={pageIsScrolled}
              ready={!waiting}
            />
          </HeaderLogo>

          <HeaderUwcLogo
            ready={!waiting}
            extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
            scrolled={pageIsScrolled}
          >
            <PageTransitionLink to={'/'} mask={false}>
              <UwcLogo fill="currentColor" />
            </PageTransitionLink>
          </HeaderUwcLogo>

          <HeaderNav>
            <HeaderMenu>
              <HeaderMenuItem
                delay={1}
                longDelay={3.6}
                extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
                scrolled={pageIsScrolled}
                ready={!waiting}
              >
                <PageTransitionLink to={routes.home}>
                  <Cta>{overview}</Cta>
                </PageTransitionLink>
              </HeaderMenuItem>

              <HeaderMenuItem
                delay={1.1}
                longDelay={3.7}
                extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
                scrolled={pageIsScrolled}
                ready={!waiting}
              >
                <PageTransitionLink to={routes.expeditions}>
                  <Cta>{expeditions}</Cta>
                </PageTransitionLink>
              </HeaderMenuItem>

              <HeaderMenuItem
                delay={1.2}
                longDelay={3.8}
                extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
                scrolled={pageIsScrolled}
                ready={!waiting}
              >
                <PageTransitionLink to={routes.itinerary}>
                  <Cta>{itinerary}</Cta>
                </PageTransitionLink>
              </HeaderMenuItem>

              <HeaderMenuItem
                delay={1.3}
                longDelay={3.9}
                extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
                scrolled={pageIsScrolled}
                ready={!waiting}
              >
                {pathname === withPrefix('/') ? (
                  <Button
                    href="#faq"
                    color={colors.light}
                    onClick={scrollToFaq}
                  >
                    {faq}
                  </Button>
                ) : (
                  <PageTransitionLink to={`${routes.home}?faq`}>
                    <Cta>{faq}</Cta>
                  </PageTransitionLink>
                )}
              </HeaderMenuItem>
            </HeaderMenu>
          </HeaderNav>

          <HeaderNav mobile={true}>
            <HeaderVerticalLine
              delay={1.4}
              left={true}
              longDelay={4}
              mobile={false}
              extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
              scrolled={pageIsScrolled}
              ready={!waiting}
            />
            <HeaderMenu>
              <HeaderMenuItem
                delay={1.4}
                longDelay={4}
                extraDelay={pathname === withPrefix('/') && !pageHasScrolled}
                ready={!waiting}
              >
                {pathname === withPrefix('/') ? (
                  <Button
                    href="#ways-to-book"
                    variant="fill"
                    color={colors.orange}
                    onClick={scrollToWtb}
                  >
                    {callToReserve}
                  </Button>
                ) : (
                  <PageTransitionLink to={`${routes.home}?ways-to-book`}>
                    <Button variant="fill" color={colors.orange}>
                      {callToReserve}
                    </Button>
                  </PageTransitionLink>
                )}
              </HeaderMenuItem>
            </HeaderMenu>
          </HeaderNav>
        </HeaderMain>

        <MobileNav open={mobileNavIsOpen}>
          <MobileNavMain>
            <MobileMenu>
              <MobileMenuItem>
                <PageTransitionLink to={routes.home}>
                  <Heading2>{overview}</Heading2>
                </PageTransitionLink>
              </MobileMenuItem>

              <MobileMenuItem>
                <PageTransitionLink to={routes.expeditions}>
                  <Heading2>{expeditions}</Heading2>
                </PageTransitionLink>
              </MobileMenuItem>

              <MobileMenuItem>
                <PageTransitionLink to={routes.itinerary}>
                  <Heading2>{itinerary}</Heading2>
                </PageTransitionLink>
              </MobileMenuItem>

              <MobileMenuItem
                delay={
                  pathname === withPrefix('/') && !pageHasScrolled ? 3.9 : 1.3
                }
              >
                {pathname === withPrefix('/') ? (
                  <a aria-label={faq} href={`#faq`} onClick={scrollToFaq}>
                    <Heading2>{faq}</Heading2>
                  </a>
                ) : (
                  <PageTransitionLink to={`${routes.home}?faq`}>
                    <Heading2>{faq}</Heading2>
                  </PageTransitionLink>
                )}
              </MobileMenuItem>
            </MobileMenu>
          </MobileNavMain>
          <HeaderUwcLogoMobile>
            <PageTransitionLink to={routes.home} mask={false}>
              <UwcLogo />
            </PageTransitionLink>
          </HeaderUwcLogoMobile>
        </MobileNav>
      </HeaderInner>
    </HeaderContainer>
  )
}

Header.propTypes = {
  pathname: PropTypes.string,
}

export default Header
